import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function HeroSection() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white border-b border-slate-200' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <a href="/" className={`text-xl font-semibold tracking-tight transition-colors ${scrolled ? 'text-dark-primary' : 'text-white'}`}>
            ARVAKON
          </a>
          <nav className="flex items-center gap-8">
            <a href="/" className={`text-sm font-medium transition-colors ${scrolled ? 'text-dark-primary' : 'text-white'}`}>
              Home
            </a>
            <a href="/services" className={`text-sm transition-colors ${scrolled ? 'text-slate-600 hover:text-dark-primary' : 'text-white/80 hover:text-white'}`}>
              Services
            </a>
            <a href="/standards" className={`text-sm transition-colors ${scrolled ? 'text-slate-600 hover:text-dark-primary' : 'text-white/80 hover:text-white'}`}>
              Standards
            </a>
            <a href="/projects" className={`text-sm transition-colors ${scrolled ? 'text-slate-600 hover:text-dark-primary' : 'text-white/80 hover:text-white'}`}>
              Industry Applications
            </a>
            <a href="/contact" className={`text-sm transition-colors ${scrolled ? 'text-slate-600 hover:text-dark-primary' : 'text-white/80 hover:text-white'}`}>
              Contact
            </a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-40 pb-32 px-8 bg-dark-primary">
        {/* Subtle gradient overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/20 via-transparent to-transparent pointer-events-none"></div>

        {/* Faint grid texture - reduced opacity */}
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: 'linear-gradient(#cbd5e1 1px, transparent 1px), linear-gradient(90deg, #cbd5e1 1px, transparent 1px)', backgroundSize: '64px 64px' }}></div>

        <div className="relative max-w-6xl mx-auto">
          {/* Top Label Bar */}
          <div className="flex items-center justify-center mb-16">
            <div className="inline-flex items-center gap-3 px-7 py-3 bg-white/5 border border-industrial-lighter/20 rounded-full backdrop-blur-sm">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shield-check-line text-industrial-lighter text-base"></i>
              </div>
              <span className="text-industrial-lighter text-xs font-medium tracking-[0.15em] uppercase">
                Independent Technical Verification Services
              </span>
            </div>
          </div>

          {/* Main Title */}
          <h1 className="text-center font-serif font-normal text-5xl text-white mb-8 tracking-tight leading-[1.2] max-w-4xl mx-auto">
            Independent Technical Verification for Industrial Manufacturing
          </h1>

          {/* Subtitle */}
          <p className="text-center text-industrial-light text-base font-light max-w-3xl mx-auto mb-14 leading-[1.8]">
            Third-party surveillance services for pressure equipment fabrication, structural steel manufacturing, and protective coating application. Services provided in accordance with ASME, ISO, EN, and NORSOK standards.
          </p>

          {/* Authority Positioning Block */}
          <div className="max-w-3xl mx-auto mb-14 text-center">
            <p className="text-industrial-lighter/50 text-[10px] font-medium tracking-[0.2em] uppercase mb-6">
              Independent Technical Positioning
            </p>
            <p className="text-industrial-light text-base font-light leading-[1.8] mb-5">
              Arvakon operates under defined contractual mandates to provide structured third-party technical verification across complex industrial manufacturing supply chains.
            </p>
            <p className="text-industrial-light text-base font-light leading-[1.8] mb-5">
              Activities are limited to independent observation, inspection witness, documentation review, and formal reporting against applicable codes, standards, and project-specific requirements.
            </p>
            <p className="text-industrial-light text-base font-light leading-[1.8]">
              Arvakon does not perform regulatory certification, notified body functions, or statutory approval services. Final acceptance authority and compliance responsibility remain with the equipment purchaser or designated authority.
            </p>
          </div>

          {/* Button Group */}
          <div className="flex items-center justify-center gap-4 mb-20">
            <button
              onClick={() => navigate('/contact')}
              className="bg-industrial-charcoal text-white px-9 py-3.5 rounded-md font-medium text-sm tracking-wide hover:bg-industrial-steel transition-colors whitespace-nowrap cursor-pointer border border-industrial-steel/40"
            >
              Request Technical Information
            </button>
            <button
              onClick={() => navigate('/services')}
              className="bg-transparent border border-industrial-lighter/25 text-industrial-lighter px-9 py-3.5 rounded-md font-medium text-sm tracking-wide hover:border-industrial-lighter/50 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
            >
              Technical Scope
            </button>
          </div>

          {/* Technical Schematic Illustration */}
          <div className="max-w-5xl mx-auto">
            <img
              src="https://public.readdy.ai/ai/img_res/9e58442674d072ccbbd986e3dae742fc.jpg"
              alt="Technical verification schematic"
              className="w-full h-auto opacity-20 rounded-lg"
            />
          </div>
        </div>
      </section>
    </>
  );
}